import { useState } from "react";
import { Profile } from "../../types";
import { ContactDisplay } from "./display/ContactDisplay";
import { ContactEditForm } from "./form/ContactEditForm";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ContactCardProps {
  profile: Profile;
  refetch: () => Promise<void>;
}

export const ContactCard = ({ profile: initialProfile, refetch }: ContactCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState<Profile>(initialProfile);
  const { toast } = useToast();

  const handleSave = async () => {
    try {
      // Get current role before update
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', initialProfile.id)
        .single();

      const currentRole = roleData?.role;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          first_name: editedProfile.first_name,
          last_name: editedProfile.last_name,
          phone: editedProfile.phone,
          street: editedProfile.street,
          city: editedProfile.city,
          state: editedProfile.state,
          zip_code: editedProfile.zip_code,
          email: editedProfile.email
        })
        .eq('id', initialProfile.id);

      if (updateError) throw updateError;

      // Update user_roles table to maintain the current role
      if (currentRole) {
        const { error: roleError } = await supabase
          .from('user_roles')
          .upsert({
            user_id: initialProfile.id,
            role: currentRole
          });

        if (roleError) throw roleError;
      }

      await refetch();
      setIsEditing(false);

      toast({
        title: "Success",
        description: "Contact information updated successfully",
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update contact information",
      });
    }
  };

  const handleCancel = () => {
    setEditedProfile(initialProfile);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  return (
    <div className="space-y-6">
      {isEditing ? (
        <ContactEditForm
          editedProfile={editedProfile}
          onProfileChange={setEditedProfile}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      ) : (
        <ContactDisplay profile={initialProfile} onEdit={handleEdit} />
      )}
    </div>
  );
};